import React from 'react';
import { useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';

import {
  logActivity as logActivityService,
  logAutoCompleteActivity,
  logCompanyReviewSubmittedActivity,
  logDLPJobActivity,
  logJobUploadResumeActivity,
  logProfileUpdateActivity,
  logSMSSubscriptionActivity,
} from 'services/logActivity';
import { openInCurrentTab,openInNewTab } from 'utils';
import { JOB_LEVELS, TRACKING, USER_ACTIVITY_EVENTS } from 'utils/constant';

const generateUniqueId = () => `${uuid().substring(0, 4)}${Date.now()}`;

export const useTracking = (options = {}) => {
  const { jobGroup, jobResponse } = options;

  const jobs = useSelector((state) => (jobGroup ? state.jobs?.[jobGroup]?.jobs : []));

  const apiParams = useSelector(({ jobs }) => (jobGroup ? jobs?.[jobGroup]?.apiParams : undefined));

  const [pageJobTitle, pageCity, pageState] = useSelector(({ locals }) => {
    const { criteria } = locals;
    const metadata = [];
    metadata.push(criteria.title || undefined);
    metadata.push(criteria.city || undefined);
    metadata.push(criteria.state || undefined);
    return metadata;
  });

  const revenueImpactingActivities = [
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_VIEWED_DLP,
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_JOB_CLICKED,
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_JOB_CLICKED_DLP,
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_APPLY_CLICK_DLP,
  ];

  const activitiesForEasyApply = [
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_VIEWED_DLP,
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_APPLY_CLICK_DLP,
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_RESUME_SUBMITTED,
  ];

  const transformJobLevelsToParams = (jobLevels) =>
    jobLevels?.map((lvl) => JOB_LEVELS?.[lvl])?.filter(Boolean) || [];

  const getCommonFields = (job = {}) =>
    !_isEmpty(job)
      ? {
          cpc: job.cpc,
          city: job.OBJcity,
          jobTitle: job.OBJtitle,
          companyID: job.companyID,
          stateCode: job.OBJstateCode,
          listingHash: job.listingHash,
          companyName: job.companyName,
          postingDate: job.postingDate,
          jobTitleDisplay: job.jobTitle,
          unifiedZippiaSalary: job?.unifiedZippiaSalary ?? 0,
          remote: !!(job?.OBJjobTags?.length && job?.OBJjobTags.indexOf('Remote') !== -1),
          jobLevels: transformJobLevelsToParams(job.jobLevels),
        }
      : {};

  const getJobFields = (job = {}, allJobs = [], sendEasyApply = false) => {
    const allUIJobs = allJobs;
    const index = allUIJobs.findIndex((j) => j.listingHash === job.listingHash);

    if (index !== -1) {
      return {
        jobLevels: transformJobLevelsToParams(job.jobLevels),
        nJob: index + 1,
        numJobs: allUIJobs.length,
        postingDate: job.postingDate,
        unifiedZippiaSalary: job.unifiedZippiaSalary ?? 0,
        previousJobListings: allUIJobs.slice(0, index).map((job2, idx) => ({
          nJob: idx + 1,
          ...(sendEasyApply ? { easyApply: !!job.easyApplyFlag } : {}),
          ...getCommonFields(job2),
        })),
      };
    } else if (!_isEmpty(job)) {
      return {
        jobLevels: transformJobLevelsToParams(job.jobLevels),
        nJob: 1,
        numJobs: 1,
        unifiedZippiaSalary: job.unifiedZippiaSalary ?? 0,
        postingDate: job.postingDate,
        previousJobListings: [],
      };
    } else {
      return {};
    }
  };

  const createPayloadForJobOpeningActivities = (activityType, job, payload = {}, allJobs = []) => {
    const index = revenueImpactingActivities.findIndex((t) => t === activityType);
    const sendEasyApply = activitiesForEasyApply.includes(activityType);
    const newPayload =
      index !== -1
        ? {
            ...payload,
            ...getJobFields(job, allJobs, sendEasyApply),
          }
        : payload;

    // Add these fields for revenueImpactingActivities activities
    if (index !== -1) {
      // Add pageJobTitle
      Object.assign(newPayload, { pageJobTitle });

      // Add pageCity
      Object.assign(newPayload, { pageCity });

      // Add pageState
      Object.assign(newPayload, { pageState });
    }
    if (apiParams && typeof apiParams === 'object') {
      try {
        newPayload.apiParams = JSON.stringify(apiParams);
      } catch (e) {
        console.error(e);
        console.error('Stringify Error Data:', apiParams);
      }
    } else if (jobResponse?.apiParams && typeof jobResponse.apiParams === 'object') {
      try {
        newPayload.apiParams = JSON.stringify(jobResponse.apiParams);
      } catch (e) {
        console.error(e);
        console.error('Stringify Error Data:', jobResponse.apiParams);
      }
    }

    if (sendEasyApply) {
      Object.assign(newPayload, { easyApply: !!job.easyApplyFlag });
    }

    return newPayload;
  };

  /**
   * Logging activity
   *
   * @param {Object} logData - logging args
   * @param {String} activityCategory - type of activity
   */
  const logActivity = (logData = {}, activityCategory = '') => {
    const {
      job,
      type,
      activityType,
      payload = {},
      pageCategory,
      jobData,
      resumeLink,
      fieldUpdated,
      fieldChangedFrom,
      fieldChangedTo,
      smsActivityType,
      smsActivityPayload = {},
    } = logData;

    const logActivityType = type || activityType;

    switch (activityCategory) {
      case TRACKING.LOG_ACTIVITY.AUTO_COMPLETE:
      case TRACKING.LOG_ACTIVITY.HEADER_SEARCH:
        return logAutoCompleteActivity(logData);
      case TRACKING.LOG_ACTIVITY.COMPANY_REVIEW_SUBMITTED:
        return logCompanyReviewSubmittedActivity();
      case TRACKING.LOG_ACTIVITY.DLP: {
        const allJobs = jobResponse?.jobs || jobs || [];
        const newPayload = createPayloadForJobOpeningActivities(
          logActivityType,
          job,
          payload,
          allJobs
        );
        return logDLPJobActivity(job, logActivityType, newPayload, pageCategory);
      }
      case TRACKING.LOG_ACTIVITY.JOB_UPLOAD_RESUME:
        return logJobUploadResumeActivity(
          {
            ...jobData,
            easyApply: !!job.easyApplyFlag,
          },
          resumeLink,
          job
        );
      case TRACKING.LOG_ACTIVITY.PROFILE_UPDATE:
        logProfileUpdateActivity(fieldUpdated, fieldChangedFrom, fieldChangedTo);
        break;
      case TRACKING.LOG_ACTIVITY.SMS_ACTIVITY:
        logSMSSubscriptionActivity(smsActivityType, smsActivityPayload);
        break;
      case TRACKING.LOG_ACTIVITY.SP_POP_OUT || TRACKING.LOG_ACTIVITY.CHAT_POP_OUT:
        logActivityService(logData?.payload ?? logData);
        break;
      default:
        return logActivityService(logData);
    }
  };

  /**
   * Open job link in new tab and log JOB_OPENING_JOB_CLICKED activity
   @param {import('types').Job} job Job object
   @param {{
    activityCategory: string;
    activityData: {
      activityType: string;
      payload?: any;
      pageCategory?: string;
    };
    returnExternalUrl?: boolean;
    sameTab?: boolean
   }} options
   */
  const openJobExternal = (job, options) => {
    /* * * * * * * * * * * * * * * * * * * * NOTE * * * * * * *  * * *  * * *  * * *  * * *  * */
    /*   if you update this method then please update the code at Server side, there we have   */
    /*   same logic at method "withClickID" at helpers/utils.js                                */
    /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

    const { OBJurl } = job || {};
    const {
      activityCategory = {},
      activityData = {},
      returnExternalUrl = false,
      sameTab = false,
    } = options || {};

    const { activityType, ...restActicityData } = activityData;

    const logData = {
      ...restActicityData,
      job: { ...(job || {}) },
    };

    if (activityType) {
      logData.activityType = activityType;
    }

    let externalUrl = OBJurl;

    const source = TRACKING.SOURCES.get(job.source);

    if (source) {
      const { param } = source;

      const url = new URL(OBJurl);
      const params = new URLSearchParams(url?.search);

      const id = generateUniqueId();

      params.has(param) ? params.set(param, id) : params.append(param, id);

      /* sometimes url might have key only not value like https://www.jobg8.com/ATSApply.aspx?It2OQbh%2Bx8BZxWN8apv6DQi  so it means search query is ?It2OQbh%2Bx8BZxWN8apv6DQi here we have key only which is 'It2OQbh%2Bx8BZxWN8apv6DQi'
            and automatically value considered '' (empty string) so url be like https://www.jobg8.com/ATSApply.aspx?It2OQbh%2Bx8BZxWN8apv6DQi='' so if you try to do search.toString() it will add '=' sign at the end automatically considering empty string value.
            so in this case search value will be ?It2OQbh%2Bx8BZxWN8apv6DQi= that's why we must have to manually join the search params. (bug/FRON-11768) */
      const query = [...params.entries()]
        .map((keyValue) =>
          keyValue
            .filter(Boolean)
            .map((x) => encodeURIComponent(x))
            .join('=')
        )
        .join('&');

      url.search = query;

      externalUrl = decodeURIComponent(url.toString());
    }

    if (externalUrl) {
      logData.job.OBJurl = externalUrl;
    }

    if (activityCategory && logData) {
      logActivity(logData, activityCategory);
    }

    if (returnExternalUrl) {
      return externalUrl;
    }
    if (sameTab) {
      openInCurrentTab(externalUrl);
    } else {
      openInNewTab(externalUrl);
    }
  };

  return {
    logActivity,
    openJobExternal,
  };
};

export const withTracking = (Component, options = {}) => {
  const RenderedComponent = (props) => {
    const tracking = useTracking({
      jobGroup: props.jobGroup || options.jobGroup,
    });

    return <Component {...props} tracking={tracking} />;
  };

  RenderedComponent.getInitialProps = Component.getInitialProps;

  return RenderedComponent;
};
